/**
 * The single transform applied to every quantity the user sees.
 *
 * One function decides what a measurement reads as, given the servings ratio
 * and the selected measurement system — and, just as importantly, reports
 * whether it *changed*. Every surface (ingredient rows, step prose, step chips,
 * cooking mode) renders through here, so a number can never be silently
 * rewritten on one screen and left alone on another.
 *
 * The `changed` flag is what drives the terracotta mark in the UI. Its baseline
 * is the recipe's OWN system, not the toggle: flipping an imperial recipe to
 * imperial changes nothing and must mark nothing.
 */

import { parseNumStr, formatQuantity } from './ingredientParser';
import {
  convertMeasure,
  formatMeasureAmount,
  canonicalizeUnit,
  unitLabel,
  formatDualTemperature,
  type MeasurementSystem,
  type TemperatureScale,
} from './unitSystem';

export interface AdjustOptions {
  /** servings / baseServings. 1 when the user has not rescaled. */
  ratio: number;
  /** The system the user selected. */
  system: MeasurementSystem;
  /** The system the recipe is written in — the baseline for "changed". */
  recipeSystem: MeasurementSystem;
  /** UI language, for spoon/cup labels ("c. à t." vs "tsp"). */
  language?: string;
}

export interface AdjustedQuantity {
  /** Formatted number for display. */
  num: string;
  /** Display unit label. */
  unit: string;
  /** True when this differs from what the recipe said — drives the mark. */
  changed: boolean;
  /** The measurement as originally written, for the reveal-on-hover. */
  original: string;
}

const isNoop = (opts: AdjustOptions) =>
  opts.ratio === 1 && opts.system === opts.recipeSystem;

/**
 * Adjust one quantity + unit pair.
 *
 * @example
 * adjustQuantity('2', 'cups', { ratio: 1.5, system: 'metric', recipeSystem: 'imperial' })
 *   // { num: '750', unit: 'ml', changed: true, original: '2 cups' }
 * adjustQuantity('1', 'tsp', { ratio: 1.5, system: 'metric', recipeSystem: 'imperial' })
 *   // { num: '1 1/2', unit: 'tsp', changed: true, original: '1 tsp' }  ← spoons stay spoons
 */
export function adjustQuantity(
  numStr: string,
  unitStr: string,
  opts: AdjustOptions
): AdjustedQuantity {
  const originalUnitLabel = unitStr.trim();
  const original = [numStr, originalUnitLabel].filter(Boolean).join(' ').trim();

  const parsed = parseNumStr(numStr);
  if (!numStr || parsed === 0) {
    // Nothing numeric to work with — never claim a change we did not make.
    return { num: numStr, unit: originalUnitLabel, changed: false, original };
  }

  const scaled = parsed * opts.ratio;
  const converted = convertMeasure(scaled, unitStr, opts.system);

  const canonical = canonicalizeUnit(unitStr);
  const displayUnit = converted.unitKey
    ? unitLabel(converted.unitKey, opts.language)
    : originalUnitLabel;
  const displayNum = converted.unitKey
    ? formatMeasureAmount(converted.amount, converted.unitKey)
    : formatQuantity(scaled, canonical.key || undefined);

  // Changed = rescaled, or the unit itself moved. Compared against the recipe's
  // own system, so selecting the system a recipe is already in marks nothing.
  const rescaled = opts.ratio !== 1;
  const changed = !isNoop(opts) && (rescaled || converted.converted);

  return { num: displayNum, unit: displayUnit, changed, original };
}

/* ------------------------------------------------------------------ *
 * Prose sweep
 * ------------------------------------------------------------------ */

export type TextSegment =
  /** Untouched prose. */
  | { type: 'text'; text: string }
  /** A rescaled/converted measurement — rendered with the mark. */
  | { type: 'measure'; text: string; original: string; changed: boolean }
  /** A temperature completed with its twin scale — an addition, never a mark. */
  | { type: 'temperature'; text: string };

/**
 * Units we are willing to rewrite inside prose. Deliberately a whitelist:
 * anything not listed here (minutes, hours, inches, centimetres, "pouces",
 * pan dimensions) is left exactly as the author wrote it.
 */
const PROSE_UNITS = [
  'millilitres', 'milliliters', 'millilitre', 'milliliter', 'mls', 'ml',
  'litres', 'liters', 'litre', 'liter', 'l',
  'cuillères à soupe', 'cuillères à café', 'cuillères à thé',
  'cuillère à soupe', 'cuillère à café', 'cuillère à thé',
  'cuilleres a soupe', 'cuillere a soupe', 'cuilleres a the', 'cuillere a the',
  'c. à soupe', 'c. a soupe', 'c. à s.', 'c. à thé', 'c. a the', 'c. à t.', 'c. à café',
  // No period, or "table" for soupe — both as printed by Quebec sites (#794).
  'c à soupe', 'c à thé', 'c à café', 'c. à table', 'c à table',
  'tablespoons', 'tablespoon', 'tbsps', 'tbsp', 'tbs',
  'teaspoons', 'teaspoon', 'tsps', 'tsp',
  'fluid ounces', 'fluid ounce', 'fl. oz', 'fl oz', 'floz',
  'tasses', 'tasse', 'cups', 'cup',
  'kilogrammes', 'kilograms', 'kilogramme', 'kilogram', 'kilos', 'kilo', 'kgs', 'kg',
  'grammes', 'grams', 'gramme', 'gram', 'gr', 'g',
  'pounds', 'pound', 'livres', 'livre', 'lbs', 'lb',
  'ounces', 'ounce', 'onces', 'once', 'ozs', 'oz',
];

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Longest alias first, so "c. à soupe" wins over any shorter prefix and
// "tablespoons" is never truncated to "tablespoon".
const UNIT_ALTERNATION = [...PROSE_UNITS]
  .sort((a, b) => b.length - a.length)
  .map(escapeRegExp)
  .join('|');

const NUMBER = String.raw`\d+(?:[.,]\d+)?(?:\s*\d+\/\d+)?|\d+\/\d+|[½¼¾⅓⅔⅛⅜⅝⅞]|\d+\s*[½¼¾⅓⅔⅛⅜⅝⅞]`;

/**
 * A quantity + whitelisted unit, with the unit closed by a word boundary so
 * "1 g" matches but the "g" in "gousse" never does.
 */
const MEASURE_RE = new RegExp(
  String.raw`(${NUMBER})\s*(${UNIT_ALTERNATION})(?![\p{L}])`,
  'giu'
);

/**
 * A temperature: a number carrying a degree sign or the word degrees/degrés,
 * so a bare "350" is never treated as an oven setting.
 *
 * The scale is frequently left implicit — NYT writes "Heat oven to 475
 * degrees", never "475 degrees F" — so it is inferred below rather than
 * required here.
 */
const TEMPERATURE_RE =
  /(\d{2,3})\s*(°|degrees?|degrés?|degres?)(?:\s*(F|C)(?:ahrenheit|elsius)?)?(?![\p{L}])/giu;

/**
 * "Rotate the pan 180 degrees" is geometry, not an oven setting. Angles are the
 * one place a degree really is just a degree.
 */
const ANGLE_AFTER = /^\s*(angle|turn|rotation|clockwise|counterclockwise)/i;
const ANGLE_BEFORE = /(rotate|rotating|turn|turning|flip|flipping)\b[^.]{0,20}$/i;

interface Match {
  start: number;
  end: number;
  segment: TextSegment;
}

function collectTemperatures(text: string): Match[] {
  const matches: Match[] = [];
  for (const m of text.matchAll(TEMPERATURE_RE)) {
    const value = parseInt(m[1], 10);
    if (!Number.isFinite(value)) continue;

    // Angles are not oven settings.
    const before = text.slice(Math.max(0, m.index! - 24), m.index!);
    const afterWord = text.slice(m.index! + m[0].length, m.index! + m[0].length + 16);
    if (ANGLE_AFTER.test(afterWord) || ANGLE_BEFORE.test(before)) continue;

    const word = (m[2] || '').toLowerCase();
    const explicit = (m[3] || '').toUpperCase();

    // Scale, in order of confidence: what the author wrote, then the language
    // of the word itself ("degrees" is a Fahrenheit convention, "degrés" a
    // Celsius one), then magnitude for a bare "190°".
    let scale: TemperatureScale;
    if (explicit) scale = explicit as TemperatureScale;
    else if (word.startsWith('degree')) scale = 'F';
    else if (word.startsWith('degr')) scale = 'C';
    else scale = value >= 250 ? 'F' : 'C';

    // Already spelled out in both scales by the author — leave it be. Claim the
    // whole span including the parenthetical, so the second reading inside it
    // is swallowed by the overlap guard rather than doubled a second time.
    const end = m.index! + m[0].length;
    const after = text.slice(end, end + 24);
    const alreadyDoubled = after.match(/^\s*\(\s*\d+\s*°?\s*(?:F|C)[^)]*\)/i);
    if (alreadyDoubled) {
      matches.push({
        start: m.index!,
        end: end + alreadyDoubled[0].length,
        segment: { type: 'text', text: m[0] + alreadyDoubled[0] },
      });
      continue;
    }

    matches.push({
      start: m.index!,
      end: m.index! + m[0].length,
      segment: { type: 'temperature', text: formatDualTemperature(value, scale) },
    });
  }
  return matches;
}

function collectMeasures(text: string, opts: AdjustOptions): Match[] {
  const matches: Match[] = [];
  for (const m of text.matchAll(MEASURE_RE)) {
    const adjusted = adjustQuantity(m[1], m[2], opts);
    if (!adjusted.changed) continue; // untouched prose stays byte-identical
    matches.push({
      start: m.index!,
      end: m.index! + m[0].length,
      segment: {
        type: 'measure',
        text: [adjusted.num, adjusted.unit].filter(Boolean).join(' '),
        original: m[0].trim(),
        changed: true,
      },
    });
  }
  return matches;
}

/**
 * Split step prose into renderable segments, rewriting quantities and doubling
 * temperatures. Returns a single text segment when nothing applies, so callers
 * can render plain text without extra work.
 *
 * Temperatures are always processed — they are independent of the selected
 * system and of the servings ratio.
 */
export function adjustQuantitiesInText(text: string, opts: AdjustOptions): TextSegment[] {
  if (!text) return [];

  const matches = [
    ...collectTemperatures(text),
    ...(isNoop(opts) ? [] : collectMeasures(text, opts)),
  ].sort((a, b) => a.start - b.start);

  if (matches.length === 0) return [{ type: 'text', text }];

  const segments: TextSegment[] = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start < cursor) continue; // overlapping match — first one wins
    if (match.start > cursor) {
      segments.push({ type: 'text', text: text.slice(cursor, match.start) });
    }
    segments.push(match.segment);
    cursor = match.end;
  }
  if (cursor < text.length) {
    segments.push({ type: 'text', text: text.slice(cursor) });
  }
  return segments;
}

/** Convenience for non-React consumers (tests, sharing, plain strings). */
export function adjustTextToString(text: string, opts: AdjustOptions): string {
  return adjustQuantitiesInText(text, opts)
    .map(s => s.text)
    .join('');
}
